import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { ReactNode } from 'react';

const WIDTH_CLASSES = {
  sm: 'sm:max-w-sm',
  md: 'sm:max-w-md',
  lg: 'sm:max-w-lg',
  xl: 'sm:max-w-xl',
  '2xl': 'sm:max-w-2xl',
  '3xl': 'sm:max-w-3xl',
  '4xl': 'sm:max-w-4xl',
  '5xl': 'sm:max-w-5xl',
  full: 'sm:max-w-full',
} as const;

const BG_CLASSES = {
  white: 'bg-white',
  base: 'bg-base',
  grey: 'bg-grey-950',
} as const;

const Z_INDEX_CLASSES = {
  default: 'z-10',
  nested: 'z-[70]',
  top: 'z-[80]',
} as const;

interface ModalDialogProps {
  editing?: boolean;
  creating?: boolean;
  onClose: () => void;
  children: ReactNode;
  title?: string;
  width?: keyof typeof WIDTH_CLASSES;
  padding?: string;
  bgColor?: keyof typeof BG_CLASSES;
  zIndex?: keyof typeof Z_INDEX_CLASSES;
  /**
   * Drop the corner X for a dialog the user must answer with one of its own
   * buttons (e.g. a required agreement). Escape and backdrop clicks still call
   * `onClose`, so such a caller makes that a no-op.
   */
  hideCloseButton?: boolean;
}

export const FormDialog = (props: ModalDialogProps) => {
  const {
    editing,
    creating,
    children,
    onClose,
    title,
    width = '4xl',
    padding = 'pt-5',
    bgColor = 'white',
    zIndex = 'default',
    hideCloseButton = false,
  } = props;

  return (
    <Dialog
      // Both flags are optional, so `false || undefined` yields `undefined`,
      // which Dialog rejects outright. Coerce so a caller that supplies only
      // one flag can still render the closed state.
      open={Boolean(editing || creating)}
      onClose={onClose}
      className={classNames('relative', Z_INDEX_CLASSES[zIndex])}
    >
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-grey-100/60 backdrop-blur-sm transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-end justify-center p-2 sm:p-4 text-center sm:items-center">
          <DialogPanel
            transition
            className={classNames([
              'relative transform rounded-xl bg-white',
              'px-4 pb-4 text-left shadow-xl transition-all data-closed:translate-y-4',
              'data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out',
              'data-leave:duration-200 data-leave:ease-in sm:my-8 w-full',
              'sm:p-6 data-closed:sm:translate-y-0 data-closed:sm:scale-95',
              'max-h-[95vh] sm:max-h-[90vh]',
              'flex flex-col',
              WIDTH_CLASSES[width],
              BG_CLASSES[bgColor],
            ])}
          >
            {!hideCloseButton && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                className="absolute top-3 right-3 sm:top-4 sm:right-4 rounded-full p-2 sm:p-1.5 text-grey-400 hover:text-grey-100 hover:bg-grey-900 transition-colors z-10"
              >
                <span className="sr-only">Close</span>
                <XMarkIcon className="size-5" aria-hidden="true" />
              </button>
            )}
            <div className="flex min-h-0 flex-1 flex-col">
              {title && (
                <div className="shrink-0 border-b border-grey-900 pb-4 text-left">
                  <DialogTitle as="h2" className="pr-8 sub-heading-sb text-grey-100">
                    {title}
                  </DialogTitle>
                </div>
              )}
              <div className={classNames(padding, 'flex-1 min-h-0 overflow-y-auto')}>
                {children}
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};
